const SPLIT_MODES = { weights: "weights", percent: "percent" };

const SPLIT_SCALE = 100;
const FULL_PERCENT = 100 * SPLIT_SCALE;
const MAX_SPLIT_VALUE = 100 * SPLIT_SCALE;

const isSplitMode = (mode) => Object.values(SPLIT_MODES).includes(mode);

const isSplitValue = (value) =>
  Number.isSafeInteger(value) && value >= 0 && value <= MAX_SPLIT_VALUE;

const sumOf = (values) => values.reduce((sum, value) => sum + value, 0);

const floorDiv = (dividend, divisor) =>
  (dividend - (dividend % divisor)) / divisor;

const splitByWeights = (totalCents, weights) => {
  const sum = sumOf(weights);
  if (sum <= 0) return weights.map(() => 0);
  const shares = weights.map((weight) => floorDiv(totalCents * weight, sum));
  let left = totalCents - sumOf(shares);
  return shares.map((share, index) => {
    if (left <= 0 || weights[index] <= 0) return share;
    left -= 1;
    return share + 1;
  });
};

const splitByPercent = (totalCents, percents) =>
  sumOf(percents) === FULL_PERCENT
    ? splitByWeights(totalCents, percents)
    : percents.map((percent) => floorDiv(totalCents * percent, FULL_PERCENT));

const splitShares = (mode, totalCents, values) =>
  mode === SPLIT_MODES.percent
    ? splitByPercent(totalCents, values)
    : splitByWeights(totalCents, values);

const byId = (ids) =>
  ids
    .map((id, index) => ({ id: Number(id), index }))
    .sort((a, b) => a.id - b.id || a.index - b.index);

const splitSharesById = (mode, totalCents, ids, values) => {
  const order = byId(ids);
  const sorted = splitShares(
    mode,
    totalCents,
    order.map(({ index }) => values[index]),
  );
  const shares = new Array(ids.length);
  order.forEach(({ index }, position) => {
    shares[index] = sorted[position];
  });
  return shares;
};

const splitErrors = {
  invalid: "invalid",
  percentSum: "percentSum",
  noWeight: "noWeight",
};

const splitError = (mode, values) => {
  if (!isSplitMode(mode) || !Array.isArray(values)) return splitErrors.invalid;
  if (values.length === 0 || !values.every(isSplitValue)) {
    return splitErrors.invalid;
  }
  const sum = sumOf(values);
  if (mode === SPLIT_MODES.percent && sum !== FULL_PERCENT) {
    return splitErrors.percentSum;
  }
  if (mode === SPLIT_MODES.weights && sum === 0) return splitErrors.noWeight;
  return null;
};

const sharesMatchSplit = (mode, totalCents, ids, values, shares) => {
  const expected = splitSharesById(mode, totalCents, ids, values);
  return (
    expected.length === shares.length &&
    expected.every((share, index) => share === shares[index])
  );
};

const retainedSplit = (mode, stored, totalCents, receivers) => {
  if (!isSplitMode(mode) || !Array.isArray(stored) || !Array.isArray(receivers)) return null;
  const valueById = new Map(stored.map(({ id, value }) => [Number(id), Number(value)]));
  const shareById = new Map(receivers.map(({ id, share }) => [Number(id), share]));
  if (shareById.size !== receivers.length || valueById.size !== stored.length) return null;
  if (![...shareById.keys()].every((id) => valueById.has(id))) return null;
  const ids = [...valueById.keys()];
  const values = ids.map((id) => valueById.get(id));
  if (splitError(mode, values) !== null) return null;
  if (ids.some((id) => !shareById.has(id) && valueById.get(id) !== 0)) return null;
  const expected = splitSharesById(mode, totalCents, ids, values);
  const matches = ids.every((id, index) => expected[index] === (shareById.get(id) ?? 0));
  return matches
    ? { mode, values: receivers.map(({ id }) => valueById.get(Number(id))) }
    : null;
};

module.exports = {
  SPLIT_MODES,
  SPLIT_SCALE,
  FULL_PERCENT,
  MAX_SPLIT_VALUE,
  isSplitMode,
  isSplitValue,
  splitByWeights,
  splitByPercent,
  splitShares,
  splitSharesById,
  splitErrors,
  splitError,
  sharesMatchSplit,
  retainedSplit,
};
